<template>
  <SidePopup
    :title="title"
    v-on:close="onClose"
    @submit="onSubmit"
    :loading="isLoading"
    routeback="/fornecedor/cadastro/imagens-produto"
  >
    <p class="imagem-produto-subtitle mb-4">
      Envie imagens para vincular aos produtos do catálogo.
    </p>

    <div class="imagem-produto-layout">
      <div class="imagem-produto-steps-dicas-row">
        <div class="imagem-produto-steps-col">
          <section class="imagem-produto-step">
            <div class="imagem-produto-step-header mb-2">
              <span class="imagem-produto-step-number">1</span>
              <h4 class="imagem-produto-step-title mb-0">
                Identificação dos produtos
              </h4>
            </div>
            <div class="imagem-produto-radios-row">
              <FormRadioGroup
                v-model="form.tipoImportacao"
                inline
                :radios="tipoImportacao"
                class="imagem-produto-radios"
              />
              <v-tooltip top max-width="320">
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-on="on"
                    class="imagem-produto-radio-help primario--text"
                    size="20"
                    >far fa-question-circle</v-icon
                  >
                </template>
                {{ tooltipCodigoInterno }}
              </v-tooltip>
            </div>
            <div
              v-if="form.tipoImportacao === 'CODIGO_INTERNO'"
              class="d-flex align-center flex-wrap mt-1"
            >
              <FormSwitch
                v-model="form.alterarSeparador"
                label="Tratar ' - ' como ' | '"
              />
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on" class="ml-2 primario--text" size="20"
                    >far fa-question-circle</v-icon
                  >
                </template>
                Se você utiliza o Windows, para vincular a imagem do produto é
                necessário substituir o código interno gerado com o sinal “ | ”
                dentro do backoffice, na seção Cadastros > Produtos, pelo sinal
                hífen “ - ”
              </v-tooltip>
            </div>
          </section>

          <section class="imagem-produto-step imagem-produto-step-upload">
            <div class="imagem-produto-step-header mb-2">
              <span class="imagem-produto-step-number">2</span>
              <h4 class="imagem-produto-step-title mb-0">Selecionar arquivo</h4>
            </div>
            <input
              ref="fileInput"
              type="file"
              class="imagem-produto-file-input"
              :accept="fileAccept"
              @change="onFileInputChange"
            />
            <div
              class="imagem-produto-dropzone"
              :class="{ 'imagem-produto-dropzone--active': dropOver }"
              @click="openFilePicker"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
            >
              <div class="imagem-produto-dropzone-body">
                <v-icon color="primario" size="40" class="mb-2"
                  >fas fa-cloud-upload-alt</v-icon
                >
                <p class="imagem-produto-dropzone-text mb-1">
                  Arraste seu arquivo ZIP aqui
                </p>
                <p class="imagem-produto-dropzone-or mb-2">ou</p>
                <FormButton
                  text="Selecionar arquivo"
                  type="primario"
                  outlined
                  @click.stop="openFilePicker"
                />
                <p
                  v-if="form.nameCurrentFile"
                  class="imagem-produto-file-name mt-2 mb-0"
                >
                  {{ form.nameCurrentFile }}
                </p>
              </div>
              <p class="imagem-produto-hint mb-0">
                ZIP até 100 MB • Imagens JPG, JPEG, PNG ou WEBP • Até 1 MB por
                imagem
              </p>
            </div>
          </section>
        </div>

        <ImagemProdutoDicas class="imagem-produto-dicas-col" />
      </div>

      <section class="imagem-produto-step imagem-produto-step-block">
          <div class="imagem-produto-step-header mb-3">
            <span class="imagem-produto-step-number">3</span>
            <h4 class="imagem-produto-step-title mb-0">
              Confira as orientações
            </h4>
          </div>
          <ImagemProdutoOrientacao />
      </section>
    </div>

    <template v-slot:buttons>
      <FormButton
        text="Importar imagens"
        submit
        type="submissao"
        :disabled="!form.currentFile"
      />
    </template>
  </SidePopup>
</template>

<script>
import SidePopup from "@/components/SidePopup";
import FormButton from "@/components/form/FormButton";
import FormRadioGroup from "@/components/form/FormRadioGroup";
import FormSwitch from "@/components/form/FormSwitch";
import ImagemProdutoOrientacao from "@/pages/cadastro/imagemproduto/ImagemProdutoOrientacao";
import ImagemProdutoDicas from "@/pages/cadastro/imagemproduto/ImagemProdutoDicas";
import { mapGetters } from "vuex";
import {
  IMAGEM_PRODUTO_UPLOAD,
  TIPO_IMPORTACAO,
  TYPES_ACCEPTED
} from "@/store/actions/imagemProduto";

const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;
const TOOLTIP_CODIGO_INTERNO =
  "Para utilizar esta opção o nome da imagem deve estar exatamente igual ao informado na coluna Código interno na seção Cadastros > Produtos.";

export default {
  name: "ImagemProdutoForm",
  components: {
    SidePopup,
    FormButton,
    FormRadioGroup,
    FormSwitch,
    ImagemProdutoOrientacao,
    ImagemProdutoDicas
  },
  data: () => ({
    isLoading: false,
    dropOver: false,
    form: {
      tipoImportacao: "CODIGO_BARRAS",
      alterarSeparador: false,
      nameCurrentFile: null,
      currentFile: null
    },
    fileAccept:
      ".zip,.jpeg,.jpg,.png,.webp,application/zip,application/x-zip-compressed,image/jpeg,image/png,image/webp",
    tipoImportacao: TIPO_IMPORTACAO,
    tooltipCodigoInterno: TOOLTIP_CODIGO_INTERNO
  }),
  computed: {
    ...mapGetters(["getFornecedorId", "moduloFornecedorAtivo"]),
    title() {
      return "Importar imagens de produtos";
    }
  },
  methods: {
    onClose(routeBack = false) {
      this.isLoading = false;
      this.$emit("close");
      if (routeBack) {
        this.$router.replace("/fornecedor/cadastro/imagens-produto");
      }
    },
    onSubmit() {
      if (!this.form.currentFile) {
        this.$vueOnToast.pop("error", "Selecione um arquivo para importar.");
        return;
      }
      this.isLoading = true;
      this.form.nome = this.form.nameCurrentFile
        ? this.form.nameCurrentFile
        : new Date().toISOString();
      const formData = new FormData();
      formData.append("idfornecedor", this.getFornecedorId);
      formData.append("foldername", this.form.nome);
      formData.append("tipoimportacao", this.form.tipoImportacao);
      formData.append("alterarseparador", this.form.alterarSeparador);
      formData.append("file", this.form.currentFile);
      this.$store
        .dispatch(IMAGEM_PRODUTO_UPLOAD, formData)
        .then(() => {
          this.$vueOnToast.pop(
            "success",
            "Images de produtos importadas com sucesso, aguarde o processamento!"
          );
          this.onClose(true);
        })
        .catch(err => {
          const erro =
            err.response.data.Mensagem ||
            err.response.data ||
            "Não foi possível importar as imagens de produtos";
          this.$vueOnToast.pop("error", erro);
          this.isLoading = false;
        });
    },
    openFilePicker() {
      this.$refs.fileInput.click();
    },
    onFileInputChange(event) {
      const file = event.target.files && event.target.files[0];
      if (file) {
        this.onSelectFile(file);
      }
      event.target.value = "";
    },
    onDragOver() {
      this.dropOver = true;
    },
    onDragLeave() {
      this.dropOver = false;
    },
    onDrop(event) {
      this.dropOver = false;
      const file = event.dataTransfer.files && event.dataTransfer.files[0];
      if (file) {
        this.onSelectFile(file);
      }
    },
    onSelectFile(file) {
      if (!this.getAcceptedFiles(file.type)) {
        this.$vueOnToast.pop(
          "error",
          `O formato do arquivo selecionado é inválido! (${file.type})`
        );
        this.clearSelectedFile();
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        this.$vueOnToast.pop(
          "error",
          "Arquivo superior a 100mb, selecione um arquivo menor!"
        );
        this.clearSelectedFile();
        return;
      }
      this.form.currentFile = file;
      this.form.nameCurrentFile = file.name;
    },
    clearSelectedFile() {
      this.form.nameCurrentFile = null;
      this.form.currentFile = null;
    },
    getAcceptedFiles(type) {
      return TYPES_ACCEPTED.includes(type);
    }
  }
};
</script>

<style lang="scss" scoped>
.imagem-produto-layout {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.imagem-produto-steps-dicas-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  align-items: stretch;
}

.imagem-produto-steps-col {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.imagem-produto-step-upload {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.imagem-produto-dicas-col {
  border-left: 1px solid rgba(127, 127, 127, 0.35);
  padding-left: 20px;
  margin-left: 20px;
  min-height: 0;
  display: flex;
  align-self: stretch;
}

.imagem-produto-radios-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.imagem-produto-radio-help {
  flex-shrink: 0;
  margin-bottom: 10px;
  cursor: pointer;
}

.imagem-produto-radios ::v-deep .v-input--selection-controls {
  margin-top: 0;
  padding-top: 0;
}

.imagem-produto-radios {
  flex: 0 1 auto;
}

.imagem-produto-subtitle {
  font-size: 14px;
  opacity: 0.87;
}

.imagem-produto-step-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.imagem-produto-step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 2px solid var(--primary-color, #1976d2);
  border-radius: 50%;
  font-size: 14px;
  font-weight: 600;
  color: var(--primary-color, #1976d2);
  flex-shrink: 0;
}

.imagem-produto-step-title {
  font-size: 16px;
  font-weight: 600;
}

.imagem-produto-file-input {
  display: none;
}

.imagem-produto-dropzone {
  border: 2px dashed rgba(127, 127, 127, 0.45);
  border-radius: 8px;
  padding: 20px 24px 14px;
  text-align: center;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 190px;
  transition: border-color 0.2s, background-color 0.2s;

  &:hover,
  &--active {
    border-color: var(--primary-color, #1976d2);
    background-color: rgba(25, 118, 210, 0.04);
  }
}

.imagem-produto-dropzone-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.imagem-produto-dropzone-text {
  font-size: 15px;
  font-weight: 500;
}

.imagem-produto-dropzone-or {
  font-size: 13px;
  opacity: 0.7;
}

.imagem-produto-file-name {
  font-size: 13px;
  font-weight: 600;
  word-break: break-all;
}

.imagem-produto-hint {
  font-size: 12px;
  opacity: 0.65;
  margin-top: 14px;
  padding-top: 2px;
  line-height: 1.4;
}

@media (max-width: 1100px) {
  .imagem-produto-steps-dicas-row {
    grid-template-columns: 1fr;
    row-gap: 16px;
  }

  .imagem-produto-dicas-col {
    border-left: none;
    border-top: 1px solid rgba(127, 127, 127, 0.35);
    padding-left: 0;
    padding-top: 16px;
    margin-left: 0;
  }

  .imagem-produto-dropzone {
    min-height: 180px;
    flex: none;
  }
}
</style>
